<template>
  <div class="cookie-container">
    <!-- Banner de Cookies -->
    <transition name="slide-up">
      <div class="cookie-banner" v-if="showCookieBanner" @click.self="closeBanner">
        <div class="cookie-content">
          <div class="cookie-icon">
            <i class="fas fa-cookie-bite"></i>
          </div>
          <div class="cookie-text">
            <h3>{{ t('home.cookies.banner.title') }}</h3>
            <p>
              {{ t('home.cookies.banner.text') }}
              <a href="/documentos_site/Declaração de Cookies - Uni Hospitalar.pdf" target="_blank">
                {{ t('home.cookies.banner.privacyPolicy') }}
              </a>.
            </p>
          </div>
          <div class="cookie-buttons">
            <button class="cookie-accept" @click="handleAcceptAll" :disabled="isLoading">
              <span v-if="!isLoading">{{ t('home.cookies.banner.accept') }}</span>
              <span v-else class="loading-spinner"></span>
            </button>
            <button class="cookie-reject" @click="handleRejectOptional" :disabled="isLoading">
              {{ t('home.cookies.banner.reject') }}
            </button>
            <button class="cookie-settings" @click="openSettings" :disabled="isLoading">
              {{ t('home.cookies.banner.settings') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal de Configurações de Cookies -->
    <transition name="fade">
      <div class="cookie-settings-overlay" v-if="showCookieSettings" @click.self="closeSettings">
        <div class="cookie-settings-modal">
          <div class="cookie-settings-header">
            <h3>{{ t('home.cookies.settings.title') }}</h3>
            <button class="close-button" @click="closeSettings" aria-label="Fechar">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="cookie-settings-body">
            <div class="cookie-settings-intro">
              <p>
                <i class="fas fa-cookie-bite cookie-intro-icon"></i>
                {{ t('home.cookies.settings.intro') }}
                <a href="/documentos_site/Declaração de Cookies - Uni Hospitalar.pdf" target="_blank">
                  {{ t('home.cookies.settings.cookieStatement') }}
                </a>.
              </p>
            </div>

            <!-- Cookies Essenciais (sempre ligado) -->
            <div class="cookie-option">
              <div class="cookie-option-info">
                <h4>{{ t('home.cookies.settings.essential.title') }}</h4>
                <p>{{ t('home.cookies.settings.essential.description') }}</p>
                <span class="cookie-badge">{{ t('home.cookies.settings.required') }}</span>
              </div>
              <div class="cookie-switch disabled">
                <input type="checkbox" checked disabled id="essential-cookie">
                <label for="essential-cookie" class="slider"></label>
              </div>
            </div>

            <!-- Cookies de Análise -->
            <div class="cookie-option">
              <div class="cookie-option-info">
                <h4>{{ t('home.cookies.settings.analytics.title') }}</h4>
                <p>{{ t('home.cookies.settings.analytics.description') }}</p>
              </div>
              <div class="cookie-switch">
                <input 
                  type="checkbox" 
                  v-model="localPreferences.analytics" 
                  id="analytics-cookie"
                >
                <label for="analytics-cookie" class="slider"></label>
              </div>
            </div>

            <!-- Cookies de Marketing -->
            <div class="cookie-option">
              <div class="cookie-option-info">
                <h4>{{ t('home.cookies.settings.marketing.title') }}</h4>
                <p>{{ t('home.cookies.settings.marketing.description') }}</p>
              </div>
              <div class="cookie-switch">
                <input 
                  type="checkbox" 
                  v-model="localPreferences.marketing" 
                  id="marketing-cookie"
                >
                <label for="marketing-cookie" class="slider"></label>
              </div>
            </div>
          </div>

          <div class="cookie-settings-footer">
            <button class="cookie-cancel" @click="closeSettings">
              {{ t('home.cookies.settings.cancel') }}
            </button>
            <button class="cookie-save" @click="handleSavePreferences" :disabled="isLoading">
              <span v-if="!isLoading">{{ t('home.cookies.settings.save') }}</span>
              <span v-else class="loading-spinner"></span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { useCookieManagement } from '@/composables/useCookieManagement';
import { useHomeTranslation } from '@/composables/useHomeTranslation';

export default {
  name: 'CookieBanner',
  setup() {
    const { t } = useHomeTranslation();
    const {
      showCookieBanner,
      cookiePreferences,
      acceptAllCookies,
      rejectOptionalCookies,
      saveCookiePreferences,
      initializeCookies
    } = useCookieManagement();

    const showCookieSettings = ref(false);
    const isLoading = ref(false);
    const localPreferences = reactive({
      essential: true,
      analytics: false,
      marketing: false
    });

    const openSettings = () => {
      // Sincronizar preferências locais com as salvas
      Object.assign(localPreferences, cookiePreferences.value);
      showCookieSettings.value = true;
    };

    const closeSettings = () => {
      showCookieSettings.value = false;
    };

    const closeBanner = () => {
      // Não permitir fechar apenas clicando fora
    };

    const handleAcceptAll = async () => {
      isLoading.value = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        acceptAllCookies();
      } finally {
        isLoading.value = false;
      }
    };

    const handleRejectOptional = async () => {
      isLoading.value = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        rejectOptionalCookies();
      } finally {
        isLoading.value = false;
      }
    };

    const handleSavePreferences = async () => {
      isLoading.value = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        saveCookiePreferences(localPreferences);
        closeSettings();
      } finally {
        isLoading.value = false;
      }
    };

    // Inicializar cookies ao montar
    initializeCookies();

    return {
      t,
      showCookieBanner,
      showCookieSettings,
      cookiePreferences,
      localPreferences,
      isLoading,
      openSettings,
      closeSettings,
      closeBanner,
      handleAcceptAll,
      handleRejectOptional,
      handleSavePreferences
    };
  }
};
</script>

<style scoped>
.cookie-container {
  position: relative;
}

/* ============ BANNER DE COOKIES ============ */
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  border-top: 4px solid #470404;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding: 30px 20px;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.cookie-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
}

.cookie-icon {
  font-size: 2.5rem;
  color: #470404;
  flex-shrink: 0;
  animation: cookieBounce 2s ease-in-out infinite;
}

@keyframes cookieBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.cookie-text {
  flex: 1;
  min-width: 250px;
  text-align: left;
}

.cookie-text h3 {
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  background: linear-gradient(180deg, #470404, #931414);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
}

.cookie-text p {
  margin: 0;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
}

.cookie-text a {
  color: #470404;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s ease;
}

.cookie-text a:hover {
  border-bottom-color: #470404;
  font-weight: bolder;
}

.cookie-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 250px;
  justify-content: flex-end;
}

.cookie-accept,
.cookie-reject,
.cookie-settings {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cookie-accept {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32));
  backdrop-filter: blur(20px) saturate(200%);
  -webkit-backdrop-filter: blur(20px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
  color: #470404;
}

.cookie-accept::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.cookie-accept span {
  position: relative;
}

.cookie-accept:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.95), rgba(48, 3, 3, 0.95));
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(71, 4, 4, 0.3);
}

.cookie-reject {
  position: relative;
  overflow: hidden;
  background: rgba(240, 240, 240, 0.6);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.cookie-reject::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, transparent 60%);
  pointer-events: none;
}

.cookie-reject span {
  position: relative;
}

.cookie-reject:hover:not(:disabled) {
  background: rgba(224, 224, 224, 0.8);
  border-color: rgba(0, 0, 0, 0.2);
}

.cookie-settings {
  background: transparent;
  color: #470404;
  border: 2px solid #470404;
}

.cookie-settings:hover:not(:disabled) {
  background: #470404;
  color: white;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============ MODAL DE CONFIGURAÇÕES ============ */
.cookie-settings-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  backdrop-filter: blur(3px);
}

.cookie-settings-modal {
  background: white;
  border-radius: 10px;
  max-width: 600px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cookie-settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #f0f0f0 0%, #ffffff 100%);
}

.cookie-settings-header h3 {
  margin: 0;
  font-size: 1.5rem;
  background: linear-gradient(160deg, #470404, #931414);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.close-button:hover {
  background: #f0f0f0;
  color: #333;
}

.cookie-settings-body {
  padding: 25px;
}

.cookie-settings-intro {
  margin-bottom: 30px;
  padding: 15px;
  background: #f0f0f0;
  border-left: 4px solid #470404;
  border-radius: 5px;
}

.cookie-settings-intro p {
  margin: 0;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
}

.cookie-intro-icon {
  color: #470404;
  font-size: 1.2rem;
  margin-right: 8px;
  vertical-align: middle;
}

.cookie-settings-intro a {
  color: #470404;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s ease;
  display: inline;
}

.cookie-settings-intro a:hover {
  border-bottom-color: #470404;
  font-weight: bolder;
}

.cookie-intro-icon:before {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.cookie-option {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.cookie-option:hover {
  background: #f0f0f0;
  border-color: #470404;
}

.cookie-option-info {
  flex: 1;
  margin-right: 20px;
}

.cookie-option-info h4 {
  margin: 0 0 8px 0;
  font-size: 1.05rem;
  background: linear-gradient(-135deg, #470404, #300303);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
}

.cookie-option-info p {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
}

.cookie-badge {
  display: inline-block;
  background: #470404;
  color: white;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

/* ============ TOGGLE SWITCH ============ */
.cookie-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  flex-shrink: 0;
}

.cookie-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cookie-switch.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

input:checked + .slider {
  background-color: #470404;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.cookie-switch.disabled input + .slider {
  background-color: #ddd;
  cursor: not-allowed;
}

/* ============ FOOTER DO MODAL ============ */
.cookie-settings-footer {
  display: flex;
  gap: 15px;
  padding: 20px 25px;
  border-top: 1px solid #f0f0f0;
  background: #f0f0f0;
  justify-content: flex-end;
}

.cookie-save,
.cookie-cancel {
  padding: 10px 30px;
  border: none;
  border-radius: 5px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 44px;
}

.cookie-save {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32));
  backdrop-filter: blur(20px) saturate(200%);
  -webkit-backdrop-filter: blur(20px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
  color: #470404;
}

.cookie-save::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.cookie-save span {
  position: relative;
}

.cookie-save:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.95), rgba(48, 3, 3, 0.95));
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(71, 4, 4, 0.3);
}

.cookie-cancel {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.cookie-cancel::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, transparent 60%);
  pointer-events: none;
}

.cookie-cancel span {
  position: relative;
}

.cookie-cancel:hover {
  background: rgba(240, 240, 240, 0.85);
  border-color: rgba(0, 0, 0, 0.2);
}

/* ============ TRANSIÇÕES ============ */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(100px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(100px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ============ RESPONSIVO ============ */
@media (max-width: 768px) {
  .cookie-content {
    flex-direction: column;
    gap: 15px;
  }

  .cookie-icon {
    font-size: 2rem;
  }

  .cookie-text {
    text-align: center;
  }

  .cookie-buttons {
    width: 100%;
    justify-content: center;
  }

  .cookie-settings-modal {
    margin: 20px;
  }

  .cookie-option {
    flex-direction: column;
    gap: 15px;
  }

  .cookie-option-info {
    margin-right: 0;
  }

  .cookie-settings-footer {
    flex-direction: column-reverse;
  }

  .cookie-settings-footer button {
    width: 100%;
  }
}

@media (max-width: 576px) {
  .cookie-banner {
    padding: 20px 15px;
  }

  .cookie-content {
    gap: 15px;
  }

  .cookie-text h3 {
    font-size: 1rem;
  }

  .cookie-text p {
    font-size: 0.9rem;
  }

  .cookie-buttons {
    flex-direction: column;
    min-width: auto;
    width: 100%;
  }

  .cookie-accept,
  .cookie-reject,
  .cookie-settings {
    width: 100%;
  }

  .cookie-settings-header {
    padding: 20px;
  }

  .cookie-settings-header h3 {
    font-size: 1.3rem;
  }

  .cookie-settings-body {
    padding: 20px;
  }

  .cookie-option {
    padding: 15px;
  }

  .cookie-option-info h4 {
    font-size: 1rem;
  }

  .cookie-option-info p {
    font-size: 0.85rem;
  }
}
</style>

